import React, { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

import FormInput from "../../Inputs/FormInput";
import Button from "../../Button";

import styles from "./imageManagementModalForm.module.css";
import furnisenseLogo from "../../../assets/Logo/project_logo.svg";

const buildPreviewUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const normalizedPath = value.replace(/\\/g, "/");
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

  const withUploads = normalizedPath.startsWith("uploads/")
    ? normalizedPath
    : normalizedPath.startsWith("/uploads/")
      ? normalizedPath.slice(1)
      : `uploads/${normalizedPath}`;

  return `${process.env.REACT_APP_SERVER_URL}/${withUploads}`;
};

const extractFileName = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.split("/").pop() || value;
  }
  if (value.name) return value.name;
  return "";
};

const getUuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (/^https?:\/\//i.test(src)) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const ImageManagementModalForm = ({
  formFields,
  formData,
  errors,
  readOnly,
  getFieldLabel,
  handleChange,
  onReset,
  onSave,
}) => {
  const fileInputRef = useRef(null);
  const backgroundFileRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState("color");

  const presetBackgrounds = [
    { id: "livingroom", name: "Living Room", image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop" },
    { id: "bedroom", name: "Bedroom", image: "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=200&h=150&fit=crop" },
    { id: "kitchen", name: "Kitchen", image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&h=150&fit=crop" },
    { id: "bathroom", name: "Bathroom", image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=200&h=150&fit=crop" },
    { id: "dining", name: "Dining", image: "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=200&h=150&fit=crop" },
    { id: "office", name: "Office", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=200&h=150&fit=crop" },
  ];

  const handlePresetSelect = (preset) => {
    if (!backgroundImageField || readOnly) return;
    handleChange(backgroundImageField.name, preset.image);
    if (backgroundColorField) handleChange(backgroundColorField.name, "");
  };

  const nameField =
    formFields.find((field) => field.name === "product_name") ||
    formFields.find((field) => field.type !== "file");
  const imageField =
    formFields.find((field) => field.name === "image_file") ||
    formFields.find((field) => field.type === "file");
  const backgroundColorField = formFields.find(
    (field) => field.name === "background_color",
  );
  const backgroundImageField = formFields.find(
    (field) => field.name === "background_image",
  );

  const imageValue = imageField ? formData[imageField.name] : null;
  const fileName = extractFileName(imageValue);
  const previewUrl = useMemo(() => {
    if (typeof imageValue === "string") return buildPreviewUrl(imageValue);
    return "";
  }, [imageValue]);
  const [localImagePreview, setLocalImagePreview] = useState("");

  const backgroundValue = backgroundImageField
    ? formData[backgroundImageField.name]
    : null;
  const backgroundFileName = extractFileName(backgroundValue);
  const backgroundPreviewUrl = useMemo(() => {
    if (typeof backgroundValue === "string") return buildPreviewUrl(backgroundValue);
    return "";
  }, [backgroundValue]);
  const [localBackgroundPreview, setLocalBackgroundPreview] = useState("");

  const activeBackgroundColor = backgroundColorField
    ? formData[backgroundColorField.name]
    : "";

  const colorOptions = [
    "#1f2937",
    "#b45309",
    "#f97316",
    "#f59e0b",
    "#fbbf24",
    "#fbbf24",
    "#f59e0b",
    "#334155",
    "#ef4444",
    "#b91c1c",
    "#f97316",
    "#facc15",
    "#16a34a",
    "#22c55e",
    "#84cc16",
    "#1d4ed8",
    "#2563eb",
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f472b6",
    "#fb7185",
    "#f97316",
  ];

  useEffect(() => {
    if (imageValue && typeof imageValue === "object" && imageValue.name) {
      const preview = URL.createObjectURL(imageValue);
      setLocalImagePreview(preview);
      return () => URL.revokeObjectURL(preview);
    }
    setLocalImagePreview("");
    return undefined;
  }, [imageValue]);

  useEffect(() => {
    if (backgroundValue && typeof backgroundValue === "object" && backgroundValue.name) {
      const preview = URL.createObjectURL(backgroundValue);
      setLocalBackgroundPreview(preview);
      return () => URL.revokeObjectURL(preview);
    }
    setLocalBackgroundPreview("");
    return undefined;
  }, [backgroundValue]);

  const openFileDialog = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (file) => {
    if (!imageField || readOnly) return;
    handleChange(imageField.name, file);
  };

  const openBackgroundDialog = () => {
    if (readOnly) return;
    backgroundFileRef.current?.click();
  };

  const handleBackgroundSelect = (file) => {
    if (!backgroundImageField || readOnly) return;
    handleChange(backgroundImageField.name, file);
    if (backgroundColorField) handleChange(backgroundColorField.name, "");
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (readOnly) return;
    setIsDragging(false);

    const [file] = event.dataTransfer.files || [];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!readOnly) setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const resolveImagePreview = localImagePreview || previewUrl;
  const resolveBackgroundPreview = localBackgroundPreview || backgroundPreviewUrl;
  const previewStyle = {
    backgroundColor: activeBackgroundColor || "#ffffff",
    backgroundImage: resolveBackgroundPreview ? `url(${resolveBackgroundPreview})` : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const handleDownload = async () => {
    if (!resolveImagePreview) return;

    try {
      const [mainImage, watermarkImage] = await Promise.all([
        loadImage(resolveImagePreview),
        loadImage(furnisenseLogo),
      ]);

      let canvasWidth = mainImage.naturalWidth || 1200;
      let canvasHeight = mainImage.naturalHeight || 1200;

      if (!mainImage.naturalWidth && resolveBackgroundPreview) {
        const bgImage = await loadImage(resolveBackgroundPreview);
        canvasWidth = bgImage.naturalWidth || canvasWidth;
        canvasHeight = bgImage.naturalHeight || canvasHeight;
      }

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Background color
      ctx.fillStyle = activeBackgroundColor || "#ffffff";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Background image (cover)
      if (resolveBackgroundPreview) {
        const bgImage = await loadImage(resolveBackgroundPreview);
        const bgScale = Math.max(
          canvasWidth / bgImage.naturalWidth,
          canvasHeight / bgImage.naturalHeight,
        );
        const bgW = bgImage.naturalWidth * bgScale;
        const bgH = bgImage.naturalHeight * bgScale;
        const bgX = (canvasWidth - bgW) / 2;
        const bgY = (canvasHeight - bgH) / 2;
        ctx.drawImage(bgImage, bgX, bgY, bgW, bgH);
      }

      // Main image (contain, 90% of canvas like preview)
      const maxW = canvasWidth * 0.9;
      const maxH = canvasHeight * 0.9;
      const imgScale = Math.min(
        maxW / mainImage.naturalWidth,
        maxH / mainImage.naturalHeight,
      );
      const imgW = mainImage.naturalWidth * imgScale;
      const imgH = mainImage.naturalHeight * imgScale;
      const imgX = (canvasWidth - imgW) / 2;
      const imgY = (canvasHeight - imgH) / 2;
      ctx.drawImage(mainImage, imgX, imgY, imgW, imgH);

      // Watermark (bottom-right)
      const margin = Math.max(16, canvasWidth * 0.03);
      const wmMaxW = canvasWidth * 0.18;
      const wmMaxH = canvasHeight * 0.18;
      const wmScale = Math.min(
        wmMaxW / watermarkImage.naturalWidth,
        wmMaxH / watermarkImage.naturalHeight,
        1,
      );
      const wmW = watermarkImage.naturalWidth * wmScale;
      const wmH = watermarkImage.naturalHeight * wmScale;
      const wmX = canvasWidth - wmW - margin;
      const wmY = canvasHeight - wmH - margin;
      ctx.globalAlpha = 0.6;
      ctx.drawImage(watermarkImage, wmX, wmY, wmW, wmH);
      ctx.globalAlpha = 1;

      const link = document.createElement("a");
      const downloadName = `Furnisense_${getUuid()}.png`;
      link.download = downloadName;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className={styles.editorLayout}>
      <div className={styles.previewPanel}>
        <div className={styles.previewCard} style={previewStyle}>
          {resolveImagePreview ? (
            <>
              <img className={styles.previewMainImage} src={resolveImagePreview} alt="Preview" />
              <img className={styles.previewWatermark} src={furnisenseLogo} alt="FurniSense watermark" />
              <button
                type="button"
                className={styles.previewDownload}
                onClick={handleDownload}
                aria-label="Download image"
              >
                <i className="bi bi-download" />
              </button>
            </>
          ) : (
            <div className={styles.previewPlaceholder}>Upload an image</div>
          )}
        </div>
      </div>

      <div className={styles.controlsPanel}>
        <div className={styles.infoCard}>
          <h4 className={styles.infoTitle}>Product Info</h4>
          {nameField && (
            <FormInput
              label={getFieldLabel(nameField)}
              name={nameField.name}
              type={nameField.type || "text"}
              error={errors[nameField.name]}
              value={formData[nameField.name] || ""}
              disabled={readOnly || nameField.disabled}
              onChange={(e) => handleChange(nameField.name, e.target.value)}
            />
          )}
        </div>

        {/* {imageField && (
          <div className={styles.uploadBlock}>
            <label className={styles.uploadLabel}>
              {getFieldLabel(imageField)}
            </label>

            <div
              className={classNames(styles.uploadCard, {
                [styles.uploadCardActive]: isDragging,
                [styles.uploadCardDisabled]: readOnly,
                [styles.uploadCardError]: Boolean(errors[imageField.name]),
              })}
              role="button"
              tabIndex={0}
              onClick={openFileDialog}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openFileDialog();
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className={styles.uploadIcon}>
                <i className="bi bi-cloud-arrow-up" />
              </div>
              <div className={styles.uploadTitle}>Browse image (jpg, png)</div>
              <div className={styles.uploadHint}>or drag and drop here</div>

              {fileName && (
                <div className={styles.fileName}>
                  Selected: {fileName}
                </div>
              )}
            </div>

            {errors[imageField.name] && (
              <div className={styles.errorText}>{errors[imageField.name]}</div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className={styles.hiddenFileInput}
              disabled={readOnly}
            />
          </div>
        )} */}

        <div className={styles.tabCard}>
          <div className={styles.tabHeader}>
            <button
              type="button"
              className={classNames(styles.tabButton, {
                [styles.tabButtonActive]: activeTab === "color",
              })}
              onClick={() => setActiveTab("color")}
            >
              <i className="bi bi-palette" />
              Color
            </button>
            <button
              type="button"
              className={classNames(styles.tabButton, {
                [styles.tabButtonActive]: activeTab === "background",
              })}
              onClick={() => setActiveTab("background")}
            >
              <i className="bi bi-image" />
              Background
            </button>
          </div>

          {activeTab === "color" && (
            <div className={styles.colorGrid}>
              {colorOptions.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={classNames(styles.colorSwatch, {
                    [styles.colorSwatchActive]: activeBackgroundColor === color,
                  })}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    if (backgroundImageField) handleChange(backgroundImageField.name, "");
                    if (backgroundColorField) handleChange(backgroundColorField.name, color);
                  }}
                  disabled={readOnly}
                >
                  {activeBackgroundColor === color && (
                    <i className="bi bi-check" />
                  )}
                </button>
              ))}
            </div>
          )}

          {activeTab === "background" && (
            <div className={styles.backgroundPane}>

              <div className={styles.presetSection}>
                <label className={styles.uploadLabel}>
                  <span className="fw-bold">Preset Backgrounds </span>
                </label>
                <div className={styles.presetGrid}>
                  {presetBackgrounds.map((preset) => (
                    <div
                      key={preset.id}
                      className={classNames(styles.presetCard, {
                        [styles.presetCardActive]: formData[backgroundImageField?.name] === preset.image,
                      })}
                      onClick={() => handlePresetSelect(preset)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handlePresetSelect(preset);
                      }}
                    >
                      <img src={preset.image} alt={preset.name} className={styles.presetImage} />
                      <span className={styles.presetName}>{preset.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className={styles.uploadLabel}>
                  <span className="fw-bold">Custom Background </span>
                </label>
              </div>
              <div
                className={styles.backgroundDrop}
                role="button"
                tabIndex={0}
                onClick={openBackgroundDialog}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openBackgroundDialog();
                }}
              >
                <div className={styles.backgroundUploadIcon}>
                  <i className="bi bi-cloud-arrow-up" />
                </div>
                <div className={styles.backgroundUploadText}>Upload your own background</div>
                {/* {backgroundFileName && (
                  <div className={styles.backgroundFileName}>
                    Selected: {backgroundFileName}
                  </div>
                )} */}
              </div>

              {/* {resolveBackgroundPreview && (
                <div className={styles.backgroundPreview}>
                  <img src={resolveBackgroundPreview} alt="Background preview" />
                </div>
              )} */}

              <input
                ref={backgroundFileRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={(e) => handleBackgroundSelect(e.target.files[0])}
                className={styles.hiddenFileInput}
                disabled={readOnly}
              />
            </div>
          )}
        </div>

        <div className={styles.actionRow}>
          <Button variant="light" className="w-25" onClick={onReset} disabled={readOnly || !onReset}>
            <i className="bi bi-arrow-counterclockwise" />
            Reset
          </Button>
          <Button variant="primary" className="w-25" onClick={onSave} disabled={readOnly || !onSave}>
            <i className="bi bi-check2-circle" />
            Apply & Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageManagementModalForm;
